// services/socketService.js
import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.isManuallyDisconnected = false;
  }

  /**
   * Connect to signaling server
   * @param {string} url - Server URL
   * @param {string} roomId - Meeting room ID
   * @param {string} userId - User ID
   * @param {string} userName - User name
   */
  connect(url, roomId, userId, userName) {
    if (this.socket && this.socket.connected) {
      console.log('Already connected to signaling server');
      return this.socket;
    }

    console.log('Connecting to signaling server:', { url, roomId, userId, userName });

    this.isManuallyDisconnected = false;

    this.socket = io(url, {
      query: { roomId, userId, userName },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 10000,
      forceNew: true
    });

    this.setupDefaultListeners();

    return this.socket;
  }

  /**
   * Setup default socket listeners
   */
  setupDefaultListeners() {
    this.socket.on('connect', () => {
      console.log('Connected to signaling server, socket ID:', this.socket.id);
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from signaling server:', reason);
      
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        if (!this.isManuallyDisconnected) {
          console.log('Server disconnected, attempting to reconnect...');
          this.socket.connect();
        }
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error.message);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to signaling server after', attemptNumber, 'attempts');
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Reconnection attempt', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Reconnection failed - giving up');
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
    });
  }

  /**
   * Join meeting room
   * @param {string} roomId - Room ID
   * @param {Object} userData - User data
   */
  joinRoom(roomId, userData) {
    if (this.socket && this.socket.connected) {
      console.log('Joining room:', roomId, userData);
      this.socket.emit('join-room', { roomId, ...userData });
    } else {
      console.error('Cannot join room: socket not connected');
      throw new Error('Socket not connected');
    }
  }

  /**
   * Leave meeting room
   * @param {string} roomId - Room ID
   */
  leaveRoom(roomId) {
    if (this.socket && this.socket.connected) {
      console.log('Leaving room:', roomId);
      this.socket.emit('leave-room', { roomId });
    }
  }

  /**
   * Send WebRTC offer
   * @param {string} peerId - Peer ID
   * @param {RTCSessionDescription} offer - Offer
   */
  sendOffer(peerId, offer) {
    if (this.socket && this.socket.connected) {
      console.log('Sending offer to peer:', peerId);
      this.socket.emit('offer', { peerId, offer });
    } else {
      console.error('Cannot send offer: socket not connected');
    }
  }

  /**
   * Send WebRTC answer
   * @param {string} peerId - Peer ID
   * @param {RTCSessionDescription} answer - Answer
   */
  sendAnswer(peerId, answer) {
    if (this.socket && this.socket.connected) {
      console.log('Sending answer to peer:', peerId);
      this.socket.emit('answer', { peerId, answer });
    } else {
      console.error('Cannot send answer: socket not connected');
    }
  }

  /**
   * Send ICE candidate
   * @param {string} peerId - Peer ID
   * @param {RTCIceCandidate} candidate - ICE candidate
   */
  sendIceCandidate(peerId, candidate) {
    if (this.socket && this.socket.connected) {
      // Only log candidate type, not full details to reduce console spam
      console.log(`Sending ICE candidate to peer ${peerId}:`, candidate.type || 'unknown');
      this.socket.emit('ice-candidate', { peerId, candidate });
    } else {
      console.error('Cannot send ICE candidate: socket not connected');
    }
  }

  /**
   * Send chat message
   * @param {string} message - Message text
   * @param {string} roomId - Room ID
   */
  sendMessage(message, roomId) {
    if (this.socket && this.socket.connected) {
      console.log('Sending chat message to room:', roomId);
      this.socket.emit('chat-message', { message, roomId, timestamp: Date.now() });
    } else {
      console.error('Cannot send message: socket not connected');
    }
  }

  /**
   * Update user media state
   * @param {string} roomId - Room ID
   * @param {Object} mediaState - { video: boolean, audio: boolean }
   */
  updateMediaState(roomId, mediaState) {
    if (this.socket && this.socket.connected) {
      console.log('Updating media state:', mediaState);
      this.socket.emit('media-state-change', { roomId, mediaState });
    } else {
      console.error('Cannot update media state: socket not connected');
    }
  }

  /**
   * Notify screen share state
   * @param {string} roomId - Room ID
   * @param {boolean} isSharing - Screen share state
   */
  updateScreenShareState(roomId, isSharing) {
    if (this.socket && this.socket.connected) {
      console.log('Updating screen share state:', isSharing);
      this.socket.emit('screen-share-change', { roomId, isSharing });
    } else {
      console.error('Cannot update screen share state: socket not connected');
    }
  }

  /**
   * Listen to event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
      
      // Store listener for cleanup
      if (!this.listeners.has(event)) {
        this.listeners.set(event, []);
      }
      this.listeners.get(event).push(callback);
      
      console.log(`Registered listener for event: ${event}`);
    } else {
      console.error('Cannot add listener: socket not initialized');
    }
  }

  /**
   * Listen to event once
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  once(event, callback) {
    if (this.socket) {
      this.socket.once(event, callback);
      console.log(`Registered one-time listener for event: ${event}`);
    } else {
      console.error('Cannot add listener: socket not initialized');
    }
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Optional specific callback to remove
   */
  off(event, callback) {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
        
        // Remove from stored listeners
        const listeners = this.listeners.get(event);
        if (listeners) {
          const index = listeners.indexOf(callback);
          if (index > -1) {
            listeners.splice(index, 1);
          }
        }
      } else {
        this.socket.off(event);
        this.listeners.delete(event);
      }
      
      console.log(`Removed listener for event: ${event}`);
    }
  }

  /**
   * Emit custom event
   * @param {string} event - Event name
   * @param {*} data - Data to send
   */
  emit(event, data) {
    if (this.socket && this.socket.connected) {
      console.log(`Emitting event: ${event}`);
      this.socket.emit(event, data);
    } else {
      console.error(`Cannot emit ${event}: socket not connected`);
    }
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.socket && this.socket.connected;
  }

  /**
   * Get socket ID
   * @returns {string}
   */
  getSocketId() {
    return this.socket ? this.socket.id : null;
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      console.log('Disconnecting socket...');
      
      this.isManuallyDisconnected = true;
      
      // Remove all listeners
      this.listeners.forEach((callbacks, event) => {
        this.socket.off(event);
      });
      this.listeners.clear();
      
      // Disconnect socket
      this.socket.disconnect();
      this.socket = null;
      this.reconnectAttempts = 0;
      
      console.log('Socket disconnected');
    }
  }

  /**
   * Force reconnect
   */
  forceReconnect() {
    if (this.socket) {
      console.log('Forcing reconnection...');
      this.reconnectAttempts = 0;
      this.socket.disconnect();
      this.socket.connect();
    }
  }
}

export default new SocketService();